import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import { formatLonghandDateWithWeekday, formatTime } from '@modernanimal/common/src/datetimes';
import { FilterSelections } from '@modernanimal/common/src/web/SmartBookingFlow/smartBookingFlow.helpers';
import useBreakpoint from 'use-breakpoint';

import { Slot } from './index';
import { LocationPartsFragment } from '../__generated__/graphql';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';

type AppointmentDateSlotProps = {
    isSlotSelected: boolean;
    setSelectedSlot: (slot: Slot)=> void;
    slot: Slot;
    isMobile: boolean;
}

const AppointmentDateSlot = (props: AppointmentDateSlotProps) => {
    const {
        slot, isSlotSelected, setSelectedSlot, isMobile = false
    } = props;

    const handleClick = () => setSelectedSlot(slot);

    const slotTime = formatTime(slot.start, slot.location.region.timezone);
    return (
        <SlotButton
            data-testid="timeSlotOption"
            disabled={isSlotSelected}
            onClick={handleClick}
            isMobile={isMobile}
        >
            <SlotText isSelected={isSlotSelected}>{slotTime}</SlotText>
        </SlotButton>
    );
};

type AppointmentDateSlotsProps = {
    appointmentFilters: FilterSelections;
    availableSlots?: Slot[];
    location?: LocationPartsFragment;
    selectedSlot?: Slot;
    setSelectedSlot: (slot: Slot)=> void;
}

const AppointmentDateSlots = (props: AppointmentDateSlotsProps) => {
    const {
        appointmentFilters,
        availableSlots,
        location,
        selectedSlot,
        setSelectedSlot
    } = props;

    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    if (!availableSlots) return null;

    if (availableSlots.length === 0) {
        return (
            <Fonts.Body data-testid="noAvailabilityMessage">
                No Availability at Selected Location
            </Fonts.Body>
        );
    }

    const selectedDateLabel = formatLonghandDateWithWeekday(
        appointmentFilters.selectedDate.toISO(), location?.region.timezone
    );

    return (
        <SelectAppointmentSlotContainer>
            <Fonts.LabelMedium data-testid="selectedDateLabel">
                {selectedDateLabel}
            </Fonts.LabelMedium>
            {availableSlots && (
                <SlotContainer isMobile={isMobile}>
                    {availableSlots.map((slot) => (
                        <AppointmentDateSlot
                            key={slot.id}
                            isSlotSelected={selectedSlot?.id === slot.id}
                            setSelectedSlot={setSelectedSlot}
                            slot={slot}
                            isMobile={isMobile}
                        />
                    ))}
                </SlotContainer>
            )}
        </SelectAppointmentSlotContainer>
    );
};

export default AppointmentDateSlots;

const SelectAppointmentSlotContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SlotButton = styled.button<{ isMobile: boolean }>`
    height: ${({ isMobile }) => `${isMobile ? 46 : 36}px`};
    background-color: ${colors.GREY_7};
    border-radius: 4px;
    
    &:hover {
        background-color: ${colors.GREY_6};
    }
  
    &:disabled {
        opacity: 1;
        color: ${colors.WHITE} !important;
        background-color: ${colors.PRIMARY_PERIWINKLE};
        height: ${({ isMobile }) => `${isMobile ? 46 : 36}px`};
    }
`;

const SlotContainer = styled.div<{ isMobile: boolean }>`
    display: grid;
    grid-auto-rows: 40px;
    grid-column-gap: 12px;
    ${({ isMobile }) => `grid-row-gap: ${isMobile ? '18' : '12'}px`};
    width: 101%; // Expand to 4 columns on mobile view
    ${({ isMobile }) => !isMobile && 'grid-template-columns: repeat(auto-fill, 100px);'}
`;

const SlotText = styled(Fonts.Body) <{ isSelected: boolean }>`
    color: ${({ isSelected }) => (isSelected ? colors.WHITE : colors.GREY_1)};
`;
